import { useState, useEffect, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import ScrollHero from "@/components/ScrollHero";
import LoadingScreen from "@/components/LoadingScreen";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import { useTranslation } from "@/i18n/LanguageContext";

const ProblemSection = lazy(() => import("@/components/ProblemSection"));
const ShowcaseSection = lazy(() => import("@/components/ShowcaseSection"));
const TestimonialsSection = lazy(() => import("@/components/TestimonialsSection"));
const CaseStudySection = lazy(() => import("@/components/CaseStudySection"));
const BrandEcosystemSection = lazy(() => import("@/components/BrandEcosystemSection"));
const ServicesSection = lazy(() => import("@/components/ServicesSection"));
const FounderSection = lazy(() => import("@/components/FounderSection"));
const ProcessTimeline = lazy(() => import("@/components/ProcessTimeline"));
const OffersSection = lazy(() => import("@/components/OffersSection"));
const FAQSection = lazy(() => import("@/components/FAQSection"));
const CTASection = lazy(() => import("@/components/CTASection"));

const jsonLdEn = {
  "@context": "https://schema.org",
  "@type": "ProfessionalService",
  name: "Zephr Studio",
  url: "https://zephrstudio.com",
  telephone: "+447440783051",
  email: "hello@zephrstudio.com",
  description: "Premium brand identities and conversion-focused websites for ambitious businesses. UK-based design studio with cinematic motion design.",
  areaServed: { "@type": "Country", name: "United Kingdom" },
  address: { "@type": "PostalAddress", addressCountry: "GB" },
  founder: { "@type": "Person", name: "Atanas Lapunov", jobTitle: "Creative Director" },
  aggregateRating: { "@type": "AggregateRating", ratingValue: "5.0", reviewCount: "3", bestRating: "5" },
  priceRange: "$$",
  openingHoursSpecification: { "@type": "OpeningHoursSpecification", dayOfWeek: ["Monday","Tuesday","Wednesday","Thursday","Friday"], opens: "09:00", closes: "18:00" },
  sameAs: ["https://www.instagram.com/zephrstudio", "https://www.linkedin.com/company/zephrstudio"],
};

interface IndexProps {
  langOverride?: "en" | "bg";
}

const Index = ({ langOverride }: IndexProps) => {
  const [siteReady, setSiteReady] = useState(false);
  const { lang, setLang } = useTranslation();

  useEffect(() => {
    if (langOverride && langOverride !== lang) {
      setLang(langOverride);
    }
  }, [langOverride, lang, setLang]);

  useEffect(() => {
    const timeout = setTimeout(() => setSiteReady(true), 2000);
    document.fonts.ready.then(() => setSiteReady(true));
    return () => clearTimeout(timeout);
  }, []);

  const isBg = lang === "bg";

  return (
    <>
      <Helmet>
        <title>{isBg ? "Zephr Studio — Брандинг и уеб дизайн за амбициозни бизнеси" : "Zephr Studio — Premium Brand & Web Design for Ambitious Businesses"}</title>
        <meta name="description" content={isBg ? "Професионален уеб дизайн, брандинг и печатни материали. Цялостни бранд идентичности и уебсайтове, създадени да увеличат доверието и конверсията. Базирани в UK, работим с бизнеси в България." : "Zephr Studio builds premium brand identities and conversion-focused websites for ambitious businesses. UK-based. Motion-driven design that increases trust and booking rates."} />
        <link rel="canonical" href={isBg ? "https://zephrstudio.com/bg" : "https://zephrstudio.com"} />
        <link rel="alternate" hrefLang="en" href="https://zephrstudio.com/" />
        <link rel="alternate" hrefLang="bg" href="https://zephrstudio.com/bg" />
        <link rel="alternate" hrefLang="x-default" href="https://zephrstudio.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={isBg ? "https://zephrstudio.com/bg" : "https://zephrstudio.com"} />
        <meta property="og:title" content={isBg ? "Zephr Studio — Брандинг и уеб дизайн" : "Zephr Studio — Brands & Websites That Move People"} />
        <meta property="og:description" content={isBg ? "Професионален уеб дизайн и брандинг за амбициозни бизнеси." : "Premium brand identities and high-performance websites with cinematic motion design. No templates. No compromises."} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={isBg ? "Zephr Studio — Брандинг и уеб дизайн" : "Zephr Studio — Brands & Websites That Move People"} />
        <meta name="twitter:description" content={isBg ? "Професионален уеб дизайн и брандинг за амбициозни бизнеси." : "Premium brand identities and high-performance websites with cinematic motion design. No templates. No compromises."} />
        <script type="application/ld+json">{JSON.stringify(jsonLdEn)}</script>
      </Helmet>
      <LoadingScreen ready={siteReady} />
      <main className="bg-background text-foreground min-h-screen">
        <Navbar />
        <ScrollHero />
        <Suspense fallback={null}>
          <ProblemSection />
          <ShowcaseSection />
          <TestimonialsSection />
          <CaseStudySection />
          <BrandEcosystemSection />
          <ServicesSection />
          <FounderSection />
          <ProcessTimeline />
          <OffersSection />
          <FAQSection />
          <CTASection />
        </Suspense>
      </main>
      <StickyMobileCTA />
    </>
  );
};

export default Index;
